const ACTIVITY_EVENTS = ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];
const LISTENER_OPTS = { passive: true, capture: true };

/**
 * Shared idle detector. One listener set for N subscribers; a single global
 * slot so only one tooltip opens per idle cycle (lowest priority wins,
 * then registration order).
 *
 * Inject `target` / `document` / timers for DOM-free tests.
 */
export function createIdleWatcher({
  target = typeof window !== 'undefined' ? window : null,
  document: doc = typeof document !== 'undefined' ? document : null,
  now = () => Date.now(),
  setTimeout: schedule = (...args) => setTimeout(...args),
  clearTimeout: unschedule = (id) => clearTimeout(id),
} = {}) {
  const subscribers = [];
  let nextId = 0;
  let attached = false;
  let slotId = null;
  let paused = false;

  function attach() {
    if (attached || !target?.addEventListener) return;
    ACTIVITY_EVENTS.forEach((event) => {
      target.addEventListener(event, onActivity, LISTENER_OPTS);
    });
    target.addEventListener('blur', onBlur, LISTENER_OPTS);
    target.addEventListener('focus', onFocus, LISTENER_OPTS);
    if (doc?.addEventListener) {
      doc.addEventListener('visibilitychange', onVisibility, LISTENER_OPTS);
      if (doc !== target) doc.addEventListener('blur', onBlur, LISTENER_OPTS);
    }
    attached = true;
  }

  function detach() {
    if (!attached || !target?.removeEventListener) return;
    ACTIVITY_EVENTS.forEach((event) => {
      target.removeEventListener(event, onActivity, LISTENER_OPTS);
    });
    target.removeEventListener('blur', onBlur, LISTENER_OPTS);
    target.removeEventListener('focus', onFocus, LISTENER_OPTS);
    if (doc?.removeEventListener) {
      doc.removeEventListener('visibilitychange', onVisibility, LISTENER_OPTS);
      if (doc !== target) doc.removeEventListener('blur', onBlur, LISTENER_OPTS);
    }
    attached = false;
  }

  function clearTimer(sub) {
    if (sub.timer != null) {
      unschedule(sub.timer);
      sub.timer = null;
    }
  }

  function restartTimer(sub) {
    clearTimer(sub);
    sub.due = false;
    sub.startedAt = now();
    if (paused) return;
    sub.timer = schedule(() => {
      sub.due = true;
      tryClaim();
    }, sub.delay);
  }

  function tryClaim() {
    if (slotId != null || paused) return;
    const t = now();
    const candidates = subscribers.filter((s) => (
      s.due || (s.startedAt != null && t >= s.startedAt + s.delay)
    ));
    if (candidates.length === 0) return;
    candidates.sort((a, b) => a.priority - b.priority || a.id - b.id);
    const winner = candidates[0];
    slotId = winner.id;
    winner.due = false;
    winner.onIdle?.();
  }

  function releaseSlot(sub) {
    if (slotId !== sub.id) return;
    slotId = null;
    // Next idle cycle: remaining subscribers wait their delay again.
    subscribers.forEach((s) => {
      if (s !== sub) restartTimer(s);
    });
  }

  function onActivity(event) {
    if (paused) return;
    const targetEl = event?.target;
    if (targetEl && typeof targetEl.closest === 'function' && targetEl.closest('[role="tooltip"]')) {
      return;
    }
    // pointermove resets a pending idle timer, but must not hide an already
    // visible tooltip — otherwise the user cannot reach dismiss / click.
    if (event?.type === 'pointermove' && slotId != null) return;
    slotId = null;
    subscribers.forEach((s) => {
      s.onActive?.();
      restartTimer(s);
    });
  }

  function pause() {
    if (paused) return;
    paused = true;
    slotId = null;
    subscribers.forEach((s) => {
      clearTimer(s);
      s.due = false;
      s.onActive?.();
    });
  }

  function resume() {
    if (!paused) return;
    paused = false;
    subscribers.forEach((s) => restartTimer(s));
  }

  function onVisibility() {
    if (doc?.hidden) pause();
    else resume();
  }

  function onBlur() {
    pause();
  }

  function onFocus() {
    resume();
  }

  function subscribe({ delay = 7000, priority = 0, onIdle, onActive } = {}) {
    const sub = {
      id: nextId,
      delay,
      priority,
      onIdle,
      onActive,
      due: false,
      timer: null,
      startedAt: null,
    };
    nextId += 1;
    subscribers.push(sub);
    if (subscribers.length === 1) attach();
    restartTimer(sub);

    return () => {
      clearTimer(sub);
      const idx = subscribers.indexOf(sub);
      if (idx !== -1) subscribers.splice(idx, 1);
      releaseSlot(sub);
      if (subscribers.length === 0) detach();
    };
  }

  return { subscribe };
}

export const idleWatcher = createIdleWatcher();
